/**
 * Selling the shopper's units back, and what a refused sale says. Whether a unit can be sold, what
 * it credits and why it can't ship are the platform's (`inventoryUnits.ts` in api-contract).
 * Withdrawing a unit is not here — an order reserves real stock, so it stays the tenant's own
 * server's call.
 */

import type { StorefrontTerminology } from "@loot-os/api-contract";
import type { SellInventoryResponse } from "@loot-os/client";
import { isLootApiErrorLike } from "@loot-os/react";
import { useMutation } from "@tanstack/react-query";
import { invalidateAfterInventorySale, lootClient } from "./queries.js";

/** Sells the named units for Σ their value; all or nothing, so a refusal sells none of them. */
export function useSellInventoryItems() {
  return useMutation<SellInventoryResponse, unknown, string[]>({
    mutationFn: (inventoryItemIds) => lootClient.sellInventoryItems({ inventoryItemIds }),
    onSuccess: invalidateAfterInventorySale,
  });
}

/**
 * What a refused sale says. Each cause the server names gets its own line, because they ask for
 * different things of the shopper: reload, wait for the shipment, or withdraw instead.
 */
export function saleErrorText(err: unknown, terms: StorefrontTerminology): string {
  if (isLootApiErrorLike(err) && err.code === "inventory_item_not_sellable") {
    const reason = err.details?.reason;
    if (reason === "sold") return "One of these is already sold. Reload to see what you hold.";
    if (reason === "withdrawing") {
      return "One of these is being shipped to you and can no longer be sold.";
    }
    if (reason === "no_value") return "One of these is worth nothing and can only be withdrawn.";
  }
  if (isLootApiErrorLike(err) && err.code === "not_found") {
    return `One of these is no longer in your ${terms.inventory.singular}. Reload to see what you hold.`;
  }
  if (isLootApiErrorLike(err) && err.code === "account_locked") {
    return "This account is locked, so nothing can be sold. Contact support.";
  }
  return err instanceof Error ? err.message : "the sale failed";
}
